import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, shareReplay, tap } from 'rxjs/operators';
import { WhiteLabelConfigService } from './white-label-config.service';
import { PublisherShopHttpService } from '../http-services/publisher-shop-http.service';
import { WhiteLabel } from '../shared/types/white-label.types';
import IDetailsInternal = WhiteLabel.IDetailsInternal;
import createEmptyDetailsInternal = WhiteLabel.createEmptyDetailsInternal;

@Injectable({ providedIn: 'root' })
export class WhiteLabelService {
  public _data$: BehaviorSubject<IDetailsInternal> = new BehaviorSubject<IDetailsInternal>(createEmptyDetailsInternal());

  private readonly whiteLabelConfigService: WhiteLabelConfigService;
  private readonly publisherShopHttpService: PublisherShopHttpService;

  constructor() {
    this.whiteLabelConfigService = inject(WhiteLabelConfigService);
    this.publisherShopHttpService = inject(PublisherShopHttpService);
  }

  /**
   * @deprecated Use WhiteLabelConfigService.config instead
   */
  public get data$(): Observable<IDetailsInternal> {
    return this._data$.pipe(shareReplay());
  }

  public get shopId$(): Observable<string> {
    return this.data$.pipe(map(data => data.shopId));
  }

  /**
   * @deprecated Use WhiteLabelConfigService.config instead
   */
  public get dataSnapshot(): IDetailsInternal {
    return this._data$.getValue();
  }

  /**
   * @deprecated Use WhiteLabelConfigService.isWhiteLabelContext instead
   */
  public get isWhiteLabel(): boolean {
    return !!this.dataSnapshot.id;
  }

  public get isWhiteLabelInpostFresh() {
    return this.dataSnapshot.name === 'InPost Fresh';
  }

  public get isWhiteLabelEmpik() {
    return this.dataSnapshot.name?.toLowerCase().includes('empik');
  }

  public async initWhiteLabelInfo(): Promise<IDetailsInternal | null> {
    try {
      return this.fetchShopByWhiteLabel()
        .pipe(
          tap(details => this._data$.next(details)),
        )
        .toPromise();
    } catch (error) {
      return null;
    }
  }

  private fetchShopByWhiteLabel = (): Observable<IDetailsInternal> => {
    const whiteLabel = this.whiteLabelConfigService.config;
    const whiteLabelResponse$ = this.whiteLabelConfigService.isWhiteLabelContext
      ? this.publisherShopHttpService.getShopIdsByWhiteLabelHash(whiteLabel.hash)
      : of(['']);

    return whiteLabelResponse$
      .pipe(
        tap(whiteLabelShopIds => {
          if (whiteLabelShopIds.length === 0) {
            throw new Error('There is no shop assigned to this domain');
          }
        }),
        map(([whiteLabelShopId]) => ({
          ...whiteLabel,
          shopId: whiteLabelShopId,
        })),
      );
  }
}
